import { Extension } from "@tiptap/core";
import { joinBackwardAcrossWrappers, joinForwardAcrossWrappers } from "./joinAcrossWrappers";

/**
 * Backspace/Delete on an empty paragraph actually removes it.
 *
 * The email editor wraps the body in isolating layout nodes and the suggested-
 * times widget is isolating too. ProseMirror's default Backspace tries to join
 * across that boundary, fails, and the empty line stays until you select it.
 * TrailingNode then re-inserts a paragraph after a widget — we skip that when
 * the user just deleted it so the extra line doesn't bounce back.
 *
 * Non-empty lines split across `<div>` wrappers get joined too — see
 * joinAcrossWrappers.
 */
export const EmptyBlockKeys = Extension.create({
    name: "emptyBlockKeys",

    addKeyboardShortcuts() {
        const removeEmpty = () => {
            const { state, view } = this.editor;
            const { $from, empty } = state.selection;
            if (!empty) return false;
            const parent = $from.parent;
            if (!parent.isTextblock || parent.content.size > 0) return false;
            if ($from.parentOffset !== 0) return false;

            const depth = $from.depth;
            if (depth < 1) return false;
            // Empty list items have their own exit-on-Backspace handling.
            if ($from.node(depth - 1).type.name === "listItem") return false;

            const container = $from.node(depth - 1);
            if (container.childCount <= 1) return false;

            const from = $from.before();
            const to = from + parent.nodeSize;
            const tr = state.tr.delete(from, to);
            tr.setMeta("skipTrailingNode", true);
            view.dispatch(tr.scrollIntoView());
            return true;
        };

        const joinAcross = (join: typeof joinBackwardAcrossWrappers) => () => {
            const tr = join(this.editor.state);
            if (!tr) return false;
            this.editor.view.dispatch(tr);
            return true;
        };
        const joinBackward = joinAcross(joinBackwardAcrossWrappers);
        const joinForward = joinAcross(joinForwardAcrossWrappers);

        return {
            Backspace: () => removeEmpty() || joinBackward(),
            Delete: () => removeEmpty() || joinForward(),
        };
    },
});
